/**
 * Derives a contrast-guaranteed, text-bearing accent from an arbitrary tenant hex.
 *
 * Why this exists: a tenant picks a single accent colour in the branding admin, and
 * the app then paints buttons with it. `#ff6600` under white text is 2.9:1 — a WCAG
 * failure that ships today. CSS cannot fix this at runtime: `color-mix()` can shift a
 * colour but cannot *guarantee* a ratio, and `color-contrast()` is not shipped in any
 * browser. So the guarantee is computed here, in TypeScript, and injected as CSS
 * custom properties by the tenant layout.
 *
 * The output separates two roles that the current code conflates:
 *   `accent`        decorative only — stripes, rings, hairlines, hover borders
 *   `accentStrong`  anything bearing text — always >=4.5:1 against `accentForeground`
 */

import { ASTROMANIA_BRAND } from "@lms/backend/lib/platform/astromania-brand";
import {
  AA_NORMAL_TEXT,
  contrastRatio,
  parseHex,
  type Rgb,
} from "@lms/backend/lib/design/contrast";

/**
 * MT-1 TEN-005 (Plan 6 S15, FR-26): the accent of a school that has chosen none: a neutral slate
 * that derives AA-compliant tokens in both themes. Astromania's orange is its own data.
 */
export const NEUTRAL_TENANT_ACCENT = "#475569";

export type ThemeMode = "light" | "dark";

export type AccentTokens = {
  /** The tenant's chosen hex, normalised. Decorative use only. */
  accent: string;
  /** Text-bearing fill. Guaranteed >=4.5:1 against `accentForeground`. */
  accentStrong: string;
  /** Hover state — a further step in the same direction, still compliant. */
  accentStrongHover: string;
  /** The colour that must be used for text sitting on `accentStrong`. */
  accentForeground: string;
  /**
   * Accent-coloured *text* on the page — eyebrows, inline links, badge labels.
   * A distinct role from `accentStrong`, which is solved against its own
   * foreground rather than against the ground it sits on: for Astromania orange
   * the two differ, and `accentStrong` lands at 4.22:1 on cream.
   */
  accentInk: string;
};

const WHITE = "#ffffff";

/** The grounds accent text has to survive: page, card, and the soft accent tint. */
const GROUNDS: Record<ThemeMode, string[]> = {
  light: [ASTROMANIA_BRAND.cream, WHITE, "#ffefe4"],
  dark: ["#120a18", "#1c1026"],
};

/** How far each search step moves toward the target, and how many steps are allowed. */
const STEP = 0.04;
const MAX_STEPS = 25;
/** Extra travel for the hover state, on top of wherever `accentStrong` landed. */
const HOVER_STEP = 0.12;

function toHex({ r, g, b }: Rgb): string {
  const channel = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, "0");
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/**
 * Blend `from` toward `to` by `amount` (0-1) in sRGB.
 *
 * sRGB rather than oklab is deliberate here: this is a search toward a measured
 * contrast target, not a perceptual tint, so the extra machinery buys nothing and
 * the result has to be a plain hex the contrast test can re-parse.
 */
export function mixToward(from: string, to: string, amount: number): string {
  const a = parseHex(from);
  const b = parseHex(to);
  const t = Math.max(0, Math.min(1, amount));
  return toHex({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  });
}

/**
 * Walk `start` toward `target` until it clears `minimum` against `foreground`.
 * Returns the first compliant step, or the target itself if nothing else works.
 */
function searchForContrast(
  start: string,
  target: string,
  foreground: string,
  minimum: number,
): string {
  for (let step = 0; step <= MAX_STEPS; step += 1) {
    const candidate = mixToward(start, target, step * STEP);
    if (contrastRatio(foreground, candidate) >= minimum) return candidate;
  }
  return target;
}

/** As above, but the candidate must clear `minimum` against every ground given. */
function searchForContrastAll(
  start: string,
  target: string,
  grounds: string[],
  minimum: number,
): string {
  for (let step = 0; step <= MAX_STEPS; step += 1) {
    const candidate = mixToward(start, target, step * STEP);
    if (grounds.every((ground) => contrastRatio(candidate, ground) >= minimum)) return candidate;
  }
  return target;
}

/**
 * Derive the accent token set for one theme.
 *
 * Light theme drives the fill *darker* and puts white text on it; dark theme keeps
 * the fill bright and puts ink on it. That asymmetry is what lets a saturated accent
 * stay recognisably itself in both themes instead of being flattened to a safe mud.
 */
export function deriveAccentTokens(hex: string, theme: ThemeMode): AccentTokens {
  const accent = toHex(parseHex(hex));

  const accentForeground = theme === "light" ? WHITE : ASTROMANIA_BRAND.ink;
  const target = theme === "light" ? ASTROMANIA_BRAND.ink : WHITE;

  const accentStrong = searchForContrast(accent, target, accentForeground, AA_NORMAL_TEXT);

  // The hover state travels further in the same direction. Because contrast against
  // the foreground is monotonic along that path, it stays compliant by construction.
  const accentStrongHover = mixToward(accentStrong, target, HOVER_STEP);

  // Text form: walk away from the grounds it will sit on, not from a foreground.
  // Light theme darkens toward ink; dark theme lightens toward white.
  const inkTarget = theme === "light" ? ASTROMANIA_BRAND.ink : WHITE;
  const accentInk = searchForContrastAll(accent, inkTarget, GROUNDS[theme], AA_NORMAL_TEXT);

  return { accent, accentStrong, accentStrongHover, accentForeground, accentInk };
}
